import { useAuth } from "../context/AuthProvider"
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import { Tenant } from "../types/User"
import { getLocalStorageItem, removeLocalStorageItem } from "../utils/localStorageUtils"
import { fetchIdentity, toUser } from "./fetchIdentity"

const useRefreshToken = () => {
    const { setAuth, setSelectedTenant } = useAuth()
    const axiosPrivate = useAxiosPrivate()

    const getCurrentSelectedTenant = (): Tenant => {
        const tenant = getLocalStorageItem(import.meta.env.VITE_LOCAL_STORAGE_KEY_SELECTED_TENANT as string)
        return tenant && JSON.parse(tenant)
    }

    const refresh = async (): Promise<string | void> => {
        try {
            const identity = await fetchIdentity(axiosPrivate)
            const { platformAdmin } = identity

            // Same rule as LoginView: a client user without any tenant has no client access.
            const userInfo = identity.userInfo?.tenants?.length ? identity.userInfo : undefined

            // A platform administrator without a usable client user (no tenants, suspended client…)
            // still gets in, but only to the platform area.
            if (!userInfo && !platformAdmin) {
                throw identity.userError ?? new Error('El usuario no tiene ningún perfil asociado')
            }

            if (userInfo) {
                const selectedTenant: Tenant = getCurrentSelectedTenant()

                if (selectedTenant && userInfo.tenants.some(tenant => tenant.id === selectedTenant.id)) {
                    setSelectedTenant(selectedTenant)
                } else {
                    setSelectedTenant(null)
                    removeLocalStorageItem(import.meta.env.VITE_LOCAL_STORAGE_KEY_SELECTED_TENANT as string)
                }
            }

            setAuth((prev) => {
                return {
                  ...prev,
                  permissions: userInfo?.permissions ?? [],
                  user: userInfo ? toUser(userInfo) : undefined,
                  tenants: userInfo?.tenants ?? [],
                  platformAdmin,
                }
            })
        } catch (error: any) {
            if (error?.code === 'NO_ACTIVE_MSAL_ACCOUNT' || error?.silent) {
                throw error
            }
            console.error("Error refreshing token:", error)
            throw error
        }
    }
    return refresh
}

export default useRefreshToken
